import React from 'react'
import { VerbalLogo } from '@/components/verbal-logo'

export interface AuthTwoPaneProps {
  /** Content for the right-hand editorial pane. Hidden on mobile. */
  editorial: React.ReactNode
  /** The form content — rendered in the narrow left pane. */
  children: React.ReactNode
  /** Optional extra classes on the outer wrapper. */
  className?: string
}

/**
 * AuthTwoPane
 *
 * Two-column editorial shell for all auth pages.
 * - Left pane (5fr): narrow form container, max-w-[420px], vertically centred.
 * - Right pane (7fr): editorial value-prop. Hidden below lg breakpoint.
 *
 * Pattern: broadsheet editorial split (Phase 8, V2 Broadsheet epic).
 * Uses BroadsheetEditorialPane conventions — 1px vertical rule separator,
 * vellum background on the editorial side.
 *
 * Purely presentational — all form logic lives in the page file.
 */
export function AuthTwoPane({
  editorial,
  children,
  className = '',
}: AuthTwoPaneProps): React.ReactElement {
  return (
    <main
      className={[
        'flex min-h-dvh flex-col lg:flex-row border-t-2 border-ink',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Narrow left pane — form */}
      <div className="flex flex-col items-center justify-center p-8 lg:w-5/12 lg:border-r lg:border-rule">
        <div className="w-full max-w-[420px]">
          <VerbalLogo className="mb-8 h-8" />
          {children}
        </div>
      </div>

      {/* Wide right pane — editorial / value-prop (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-7/12 flex-col justify-center gap-6 bg-vellum p-12">
        {editorial}
      </div>
    </main>
  )
}
